// src/components/ui/Message.tsx
import React, { ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Info, Check, AlertTriangle, Terminal, X } from 'lucide-react';
import { THEME } from '@/theme';

export type MessageType = 'error' | 'warning' | 'info' | 'success' | 'debug';

interface MessageProps {
  type: MessageType;
  message?: string | ReactNode | null;
  translateKey?: string | null;
  className?: string;
  values?: Record<string, string | number>;
  icon?: boolean;
  dismiss?: () => void;
  autoHide?: boolean;
  autoHideTime?: number;
}

/**
 * Unified Message component for displaying notifications of different types
 * Supports both direct messages and translation keys
 */
const Message: React.FC<MessageProps> = ({ 
  type, 
  message, 
  translateKey,
  className = '',
  values = {},
  icon = true,
  dismiss,
  autoHide = false,
  autoHideTime = 5000
}) => {
  const { t } = useTranslation();
  
  // Auto-hide functionality
  useEffect(() => {
    if (autoHide && dismiss) {
      const timer = setTimeout(() => {
        dismiss();
      }, autoHideTime);
      
      return () => clearTimeout(timer);
    }
  }, [autoHide, autoHideTime, dismiss]);
  
  // If neither message nor translateKey is provided, render nothing
  if (!message && !translateKey) return null;
  
  // For translation keys, use the translation function
  let content: ReactNode = message;
  
  // Only use translation if translateKey exists
  if (translateKey) {
    content = t(translateKey, values);
  }
  
  // Check if there's content to display
  if (!content) return null;
  
  // Add icon based on message type
  const getIcon = () => {
    if (!icon) return null;
    
    switch (type) {
      case 'error':
        return <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0" />;
      case 'info':
        return <Info className="w-5 h-5 mr-2 flex-shrink-0" />;
      case 'success':
        return <Check className="w-5 h-5 mr-2 flex-shrink-0" />;
      case 'debug':
        return <Terminal className="w-5 h-5 mr-2 flex-shrink-0" />;
      default:
        return null;
    }
  };

  // Get styles based on message type from theme
  const getTypeStyles = () => {
    return THEME.messages[type] || '';
  };
  
  return (
    <div 
      className={`${getTypeStyles()} ${className} flex items-start relative`}
      role="alert"
    >
      {getIcon()}
      <div className="flex-1">{content}</div>
      {dismiss && (
        <button 
          onClick={dismiss}
          className="ml-2 -mr-1 text-current opacity-70 hover:opacity-100 p-1 rounded-full"
          aria-label={t('common.close')}
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

export default Message;